/**
 * 좌표 변환 유틸리티
 * 화면 좌표 ↔ 캔버스 좌표 변환
 */

import { CANVAS, IMAGE_DISPLAY } from '../constants/plancheConstants';

/**
 * 화면 픽셀 → 캔버스 픽셀 변환
 * @param {number} screenX - 화면 X 좌표
 * @param {number} screenY - 화면 Y 좌표
 * @param {number} screenWidth - 화면 컨테이너 너비
 * @param {number} screenHeight - 화면 컨테이너 높이
 * @returns {{ x: number, y: number }} 캔버스 좌표
 */
export const screenToCanvas = (screenX, screenY, screenWidth, screenHeight) => {
    const scaleX = CANVAS.BASE_WIDTH / screenWidth;
    const scaleY = CANVAS.BASE_HEIGHT / screenHeight;
    return {
        x: screenX * scaleX,
        y: screenY * scaleY,
    };
};

/**
 * 캔버스 픽셀 → 화면 픽셀 변환
 * @param {number} canvasX - 캔버스 X 좌표
 * @param {number} canvasY - 캔버스 Y 좌표
 * @param {number} screenWidth - 화면 컨테이너 너비
 * @param {number} screenHeight - 화면 컨테이너 높이
 * @returns {{ x: number, y: number }} 화면 좌표
 */
export const canvasToScreen = (canvasX, canvasY, screenWidth, screenHeight) => {
    const scaleX = screenWidth / CANVAS.BASE_WIDTH;
    const scaleY = screenHeight / CANVAS.BASE_HEIGHT;
    return {
        x: canvasX * scaleX,
        y: canvasY * scaleY,
    };
};

/**
 * 이미지 표시 크기 계산 (contain 방식)
 * @param {number} imgWidth - 원본 이미지 너비
 * @param {number} imgHeight - 원본 이미지 높이
 * @param {number} containerWidth - 컨테이너 너비
 * @param {number} containerHeight - 컨테이너 높이
 * @param {number} scale - 추가 스케일 (기본 1)
 * @returns {{ width: number, height: number, fitScale: number }}
 */
export const calculateImageDisplay = (imgWidth, imgHeight, containerWidth, containerHeight, scale = 1) => {
    const maxWidth = containerWidth * IMAGE_DISPLAY.MAX_WIDTH_PERCENT;
    const maxHeight = containerHeight * IMAGE_DISPLAY.MAX_HEIGHT_PERCENT;
    const fitScale = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);

    return {
        width: imgWidth * fitScale * scale,
        height: imgHeight * fitScale * scale,
        fitScale,
    };
};

/**
 * 렌더링 시 화면 오프셋 → 캔버스 오프셋 변환
 * (화면 크기가 알려지지 않은 경우 참조 크기 사용)
 * @param {number} offsetX - 화면 오프셋 X
 * @param {number} offsetY - 화면 오프셋 Y
 * @returns {{ x: number, y: number }} 캔버스 오프셋
 */
export const screenOffsetToCanvas = (offsetX, offsetY) => {
    const ratio = CANVAS.BASE_WIDTH / CANVAS.SCREEN_REFERENCE_WIDTH;
    return {
        x: offsetX * ratio,
        y: offsetY * ratio,
    };
};

/**
 * 이미지 중앙 정렬 위치 계산
 * @param {number} containerWidth - 컨테이너 너비
 * @param {number} containerHeight - 컨테이너 높이
 * @param {number} displayWidth - 표시할 이미지 너비
 * @param {number} displayHeight - 표시할 이미지 높이
 * @param {number} offsetX - 추가 오프셋 X (기본 0)
 * @param {number} offsetY - 추가 오프셋 Y (기본 0)
 * @returns {{ x: number, y: number }} 그리기 시작 위치
 */
export const calculateCenteredPosition = (containerWidth, containerHeight, displayWidth, displayHeight, offsetX = 0, offsetY = 0) => {
    return {
        x: (containerWidth - displayWidth) / 2 + offsetX,
        y: (containerHeight - displayHeight) / 2 + offsetY,
    };
};
